import { Component, ElementRef, ViewChild } from '@angular/core';
import { UserProfile } from '../../interfaces/UserProfile';
import { AuthenticationService } from '../../services/authentication.service';
import { DateTime } from 'luxon';
import { environment } from 'src/environments/environment';
import { Router } from '@angular/router';
import { CellTwoButtonRendererComponent } from '../cellrenderers/cell-two-button-renderer/cell-two-button-renderer.component';
import RenderDriverReportRequest from '../../interfaces/renderReportRequest';
import { DriverReportView } from '../../interfaces/driverReportView';
import { RecordsService } from '../../services/records.service';
import { DriverDashboardRecord } from '../../interfaces/driverDashboardRecord';

import { DriverRecordRequestService } from '../../services/driverrecordrequest.service';
import { TranslatorService } from '../../services/translator.service';
import { GeneralService } from '../../services/general.service';
import { GridApi } from '@ag-grid-community/core';

@Component({
     selector: 'app-driver-dashboard-grid',
     templateUrl: './driver-dashboard-grid.component.html',
     styleUrls: ['./driver-dashboard-grid.component.scss'],
     standalone: false
})
export class DriverDashboardGridComponent {

     startRow: number = 1;
     pageSize: number = 8000;
     sortCol: string = 'RequestedDate';
     sortDir: string = 'desc';

     gridOptions: any = { suppressHorizontalScroll: true };
     domLayout: any = 'autoHeight';
     defaultColDef: any = {
          resizable: true,
          wrapText: true
     };
     overlayLoadingTemplate: any;
     overlayNoRowsTemplate: any = '<span class="italic" id="noResultsFound">No results found</span>';
     columnDefs: [] | any = [];
     rowData$: DriverDashboardRecord[] = [];

     voodooComplete: boolean = false;
     gridLanguage: string = '';
     lang: string = '';
     isLoading: boolean = true;

     username: string = '';
     userProfile: UserProfile | undefined;
     authMethod: string = 'Login.gov';
     accessedVia: string = 'DriverDashboard';
     customerId: string = '2147483647';
     loadingText: string = '';
     noRowsText: string = '';
     requestDateHeader: string = '';
     lastNameHeader: string = '';
     licenseNumberHeader: string = '';
     stateHeader: string = '';
     actionsHeader: string = '';
     viewButtonText: string = '';
     expiredText: string = '';
     downloadButtonText: string = '';

     private gridApi!: GridApi;

     @ViewChild('pageVooDoo')
     footerArea: ElementRef | undefined;

     constructor(
          private authenticationService: AuthenticationService,
          public router: Router,
          private recordsService: RecordsService,
          public driverRecordRequestService: DriverRecordRequestService,
          public translationService: TranslatorService,
          public generalService: GeneralService) {

          this.userProfile = this.authenticationService.getUserProfile() as UserProfile;

          this.getColumnDefs().then((r) => this.loadColumnDefs(r));
          this.lang = this.translationService.getActiveLanguage().toUpperCase();
     }
     // ********************** Lifecycle Events ********************** //
     ngOnInit(): void {
          //detect language change
          this.translationService._hasChanges$.subscribe(newValue => {
               if (newValue === true) {
                    this.translationService.resetHasChange();
                    this.lang = this.translationService.getActiveLanguage().toUpperCase();
               }
          });

          this.username = this.authenticationService.getUserName();
          this.gridLanguage = this.translationService.getActiveLanguage().toUpperCase();
          this.getColumnDefs().then((r) => this.loadColumnDefs(r));
     }

     loadColumnDefs(r: any) {
          if (r !== undefined) {
               this.columnDefs = r;
          }
     }

     onGridReady(params: any) {
          this.populateRecords();
          this.gridApi = params.api;
          this.smartSizeColumns();
     }

     // ********************** Grid Data ********************** //

     populateRecords(): any {
          const scope = this;
          const recordsPromise = this.recordsService.getRecords(this.startRow, this.pageSize, this.sortCol, this.sortDir, this.username);
          recordsPromise
               .then((response: any) => {
                    this.rowData$ = [];
                    if (response) {
                         this.rowData$ = response!.items;
                    }

                    console.log(this.footerArea)
                    if (this.footerArea) {
                         this.generalService.gridFooterTxt(this.footerArea as ElementRef);
                    }
                    scope.smartSizeColumns();
                    scope.isLoading = false;
                    return this.rowData$;
               })
               .catch((error: any) => {
                    console.error(error);
                    this.rowData$ = [];
                    scope.smartSizeColumns();
                    scope.isLoading = false;
                    return this.rowData$;
               });
     }

     async getColumnDefs() {
          const componentScope = this;
          componentScope.lang = componentScope.translationService.getActiveLanguage().toUpperCase();
          componentScope.updateTranslatedText(componentScope);

          const promise = new Promise((resolve, reject) => {

               const expired: DateTime = DateTime.now()
                    .minus({ hours: Number(environment.APP_DRIVER_RECORD_EXPIRATION_HOURS) })
                    .endOf('hour');

               let records = [
                    {
                         headerName: componentScope.requestDateHeader,
                         field: 'requestedDate',
                         valueFormatter: (params: any) => this.formatDate(params),
                         sortable: true,
                         minWidth: 155,
                         maxWidth: 180,
                         flex: 2,
                    },
                    {
                         headerName: componentScope.lastNameHeader,
                         field: 'lastName',
                         sortable: true,
                         minWidth: 150,
                         maxWidth: 180,
                         flex: 2,
                    },
                    {
                         headerName: componentScope.licenseNumberHeader,
                         field: 'driverLicenseNumber',
                         tooltipField: 'driverLicenseNumber',
                         sortable: true,
                         minWidth: 100,
                         flex: 2,
                         resizable: true,
                    },
                    {
                         headerName: componentScope.stateHeader,
                         field: 'driverLicenseState',
                         sortable: true,
                         minWidth: 80,
                         maxWidth: 80,
                         flex: 2,
                    },
                    {
                         headerName: componentScope.actionsHeader,
                         field: 'authCode',
                         sortable: false,
                         minWidth: 200,
                         flex: 3,
                         suppressSizeToFit: false,
                         cellRenderer: CellTwoButtonRendererComponent,
                         cellRendererParams: {
                              buttonText: componentScope.viewButtonText,
                              buttonTitle: componentScope.viewButtonText,
                              buttonName: function (params: any) {
                                   return 'view' + params.value;
                              },
                              buttonClass: 'btn btn-primary btn-sm mt-n1 me-2',
                              hideButton: function (params: any) {
                                   const requestDate = DateTime.fromFormat(params!.data!.requestedDate, "yyyy-MM-dd'T'HH:mm:ss");
                                   if (requestDate < expired) {

                                        /* HIDE BUTTON if: transaction is expired */
                                        return true;
                                   } else {
                                        return false;
                                   }
                              },
                              hideButtonText: componentScope.expiredText,
                              buttonClick: function (authCode: string) {
                                   const request: RenderDriverReportRequest = { asPdf: false, authCode: authCode, driverIP: '' };
                                   componentScope.driverRecordRequestService.getEndUserIp()
                                        .then((result: any) => {
                                             request.driverIP = result;
                                             const reportView: DriverReportView = {
                                                  accessedVia: componentScope.accessedVia,
                                                  accessedBy: componentScope.username,
                                                  accessorCustomerId: '2147483647',
                                                  authCode: authCode,
                                                  authenticationMethod: componentScope.authMethod,
                                                  accessedDateTime: DateTime.now().toJSDate()
                                             }
                                             componentScope.driverRecordRequestService.openDriverReport(request, reportView)
                                                  .then((v: string | any) => {
                                                       componentScope.openInBrowser(v, authCode);
                                                  })
                                                  .catch((error: any) => {
                                                       console.error(error);
                                                  });
                                        }).catch((error: any) => {
                                             console.error(error);
                                             const reportView: DriverReportView = {
                                                  accessedVia: componentScope.accessedVia,
                                                  accessedBy: componentScope.username,
                                                  accessorCustomerId: '2147483647',
                                                  authCode: authCode,
                                                  authenticationMethod: componentScope.authMethod,
                                                  accessedDateTime: DateTime.now().toJSDate()
                                             }
                                             componentScope.driverRecordRequestService.openDriverReport(request, reportView)
                                                  .then((v: string | any) => {
                                                       componentScope.openInBrowser(v, authCode);
                                                  })
                                                  .catch((error: any) => {
                                                       console.error(error);
                                                  });
                                        });
                              },
                              button2Text: componentScope.downloadButtonText,
                              button2Title: componentScope.downloadButtonText,
                              button2Name: 'download',
                              button2Class: 'btn btn-outline-primary btn-sm mt-n1',
                              hideButton2: function (params: any) {
                                   if (DateTime.fromFormat(params!.data!.requestedDate, "yyyy-MM-dd'T'HH:mm:ss") < expired) {
                                        /* HIDE BUTTON if: transaction is expired */
                                        return true;
                                   } else {
                                        return false;
                                   }
                              },
                              button2Click: function (authCode: string) {
                                   const reportView: DriverReportView = {
                                        accessedVia: componentScope.accessedVia,
                                        accessedBy: componentScope.username,
                                        accessorCustomerId: '2147483647',
                                        authCode: authCode,
                                        authenticationMethod: componentScope.authMethod,
                                        accessedDateTime: DateTime.now().toJSDate()
                                   }
                                   componentScope.driverRecordRequestService.getDriverReportPdf(authCode, reportView)
                                        .then((v: any) => componentScope.openPdf(v, authCode))
                                        .catch((error: any) => {
                                             console.error(error);
                                        });
                              },
                         }
                    },
               ];
               resolve(records);
          });
          return await promise;
     }

     // ********************** Open Driver Reports ********************** //
     openInBrowser(contentToRender: string, authCode: string) {
          if (contentToRender && contentToRender.length === 0) {
               this.router.navigateByUrl('/customerSupportInfo');
          }
          this.generalService.logActivity('driver_record::view',this.userProfile?.userData.email!, 
               [{name: 'auth_code', value: authCode}], 
               [{name: 'info', value: 'PDF downloaded from Driver Dashboard.'}]);    
          const blob = new Blob([contentToRender], { type: 'text/html' });
          const url = URL.createObjectURL(blob);
          url.split("/");
          const faxWindow = window.open(url);
          if (faxWindow) {
               faxWindow.onload = () => {
                    URL.revokeObjectURL(url);
               };
               faxWindow?.focus();
          } else {
               // If popup blocked
               console.warn('Popup blocked. Please allow popups for this site.');
               // Revoke even if not opened to prevent memory leaks
               URL.revokeObjectURL(url); 
          }
     }

     openPdf(buffer: any, authCode: string) {
          this.generalService.logActivity('driver_record::view',this.userProfile?.userData.email!, 
               [{name: 'auth_code', value: authCode}], 
               [{name: 'info', value: 'PDF downloaded from Driver Dashboard.'}]);
          const filename = "DriverReport.pdf";
          const element = document.createElement('a');
          element.setAttribute('href', 'data:application/octet-stream;base64,' + buffer);
          element.setAttribute('download', filename);
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
     }

     // ********************** Formatting ********************** //
     formatDate(params: any) {
          if (params.value && params.value !== '') {
               return DateTime.fromISO(params.value).toFormat('MM/dd/yyyy');
          }
          return '';
     }

     smartSizeColumns() {
          let colsToHide = [];
          let colsToShow = [];

          if (innerWidth < 900) {
               colsToHide.push('driverLicenseState');
          } else {
               colsToShow.push('driverLicenseState');
          }

          if (innerWidth < 768) {
               colsToHide.push('driverLicenseNumber');
          } else {
               colsToShow.push('driverLicenseNumber');
          }

          if (innerWidth < 575) {
               colsToHide.push('lastName');
          } else {
               colsToShow.push('lastName');
          }

          this.gridApi.setColumnsVisible(colsToHide, false);
          this.gridApi.setColumnsVisible(colsToShow, true);
          this.gridApi.sizeColumnsToFit();
     }

     translate(translationTag: string) {
          this.lang = this.translationService.getActiveLanguage().toUpperCase();

          if (translationTag === 'Voodoo' && this.gridLanguage !== this.lang as string) {
               // Language has changed
               this.gridLanguage = this.lang;
               this.getColumnDefs().then((r) => this.loadColumnDefs(r));
               this.smartSizeColumns();
               if (this.footerArea) {
                    this.generalService.gridFooterTxt(this.footerArea as ElementRef);
               }
          }
          return this.doTranslation(translationTag);
     }

     doTranslation(translationTag: string): string {
          let translatedText: string = this.generalService.getTranslation(translationTag, 'customer')
          if (translatedText === undefined) {
               translatedText = this.generalService.getTranslation(translationTag, 'customer');
          }
          return translatedText;
     }

     private updateTranslatedText(scope: any) {
          if (scope.lang === 'ES') {
               scope.noRowsText = 'No se han encontrado resultados';
               scope.loadingText = 'Cargando...';
               scope.requestDateHeader = 'Fecha de solicitud';
               scope.lastNameHeader = 'Apellido';
               scope.licenseNumberHeader = 'Licencia';
               scope.stateHeader = 'Estado';
               scope.actionsHeader = 'Acción';
               scope.viewButtonText = 'Ver';
               scope.expiredText = 'Caducado';
               scope.downloadButtonText = 'Descargar';
          } else if (scope.lang === 'FR') {
               scope.noRowsText = 'Aucun résultat trouvé';
               scope.loadingText = 'Chargement...';
               scope.requestDateHeader = 'Date de la demande';
               scope.lastNameHeader = 'Nom de famille';
               scope.licenseNumberHeader = 'Numéro de permis';
               scope.stateHeader = 'État';
               scope.actionsHeader = 'Action';
               scope.viewButtonText = 'Afficher';
               scope.expiredText = 'Expiré';
               scope.downloadButtonText = 'Télécharger';
          } else {
               scope.loadingText = 'Loading...';
               scope.requestDateHeader = 'Request Date';
               scope.lastNameHeader = 'Last Name';
               scope.licenseNumberHeader = 'License #';
               scope.stateHeader = 'State';
               scope.actionsHeader = 'Actions';
               scope.viewButtonText = 'View';
               scope.expiredText = 'Expired';
               scope.downloadButtonText = 'Download';
          }
     }
}
